import {
  Calculator,
  Ruler,
  Triangle,
  Globe,
  Lightbulb,
  Atom,
  Target,
  FlaskConical,
  BookOpen,
  PenTool,
  Book,
  FileText,
  Brain,
  Palette,
  Music,
  Trophy,
  Microscope,
  Leaf,
  Languages,
  GraduationCap,
} from "lucide-react";

// Define a type for topic metadata
export interface TopicMetadata {
  icon: any;
  color: string;
  description: string;
}

const SUBJECT_COLORS: Record<string, string> = {
  "mathematics": "from-blue-500 to-indigo-600",
  "math": "from-blue-500 to-indigo-600",
  "language arts": "from-emerald-500 to-teal-600",
  "english": "from-emerald-500 to-teal-600",
  "science": "from-amber-500 to-orange-600",
  "social studies": "from-violet-500 to-purple-600",
  "ability test": "from-rose-500 to-pink-600",
  "subject": "from-blue-500 to-indigo-600",
};

const TOPIC_COLORS = [
  "from-blue-500 to-blue-600",
  "from-emerald-500 to-emerald-600",
  "from-violet-500 to-violet-600",
  "from-amber-500 to-amber-600",
  "from-rose-500 to-rose-600",
  "from-teal-500 to-teal-600",
  "from-indigo-500 to-indigo-600",
  "from-orange-500 to-orange-600",
  "from-pink-500 to-pink-600",
  "from-cyan-500 to-cyan-600",
];

// Icon mapping based on common keywords in topic names
const ICON_KEYWORDS: Record<string, any> = {
  mathematics: Calculator,
  math: Calculator,
  "language arts": Languages,
  english: Languages,
  "social studies": Globe,
  "ability test": Brain,
  number: Calculator,
  measurement: Ruler,
  geometry: Triangle,
  environment: Globe,
  geography: Globe,
  energy: Lightbulb,
  material: Atom,
  science: FlaskConical,
  body: Target,
  human: Target,
  mixture: FlaskConical,
  chemistry: FlaskConical,
  reading: BookOpen,
  comprehension: BookOpen,
  grammar: PenTool,
  writing: Book,
  essay: FileText,
  history: Book,
  culture: Palette,
  music: Music,
  art: Palette,
  sport: Trophy,
  biology: Microscope,
  plant: Leaf,
  algebra: Calculator,
  fraction: Calculator,
  decimal: Calculator,
  reasoning: Brain,
  verbal: Languages,
};

// Default icon for topics without specific keywords
const DEFAULT_ICON = FileText;

/**
 * Get icon for a topic based on its name
 */
export function getTopicIcon(topicName: string): any {
  const normalized = topicName.toLowerCase();
  
  // Check for keyword matches
  for (const [keyword, icon] of Object.entries(ICON_KEYWORDS)) {
    if (normalized.includes(keyword)) {
      return icon;
    }
  }
  
  return DEFAULT_ICON;
}

export function getTopicColor(topicName: string, index: number = 0): string {
  const subjectColor = SUBJECT_COLORS[topicName.toLowerCase()];
  if (subjectColor) return subjectColor;

  let hash = 0;
  for (let i = 0; i < topicName.length; i++) {
    hash = ((hash << 5) - hash) + topicName.charCodeAt(i);
    hash = hash & hash;
  }
  
  const colorIndex = Math.abs(hash) % TOPIC_COLORS.length;
  return TOPIC_COLORS[colorIndex];
}

/**
 * Fallback description when the API did not send one. The API normally supplies
 * `description` (curated per subject/topic in shared/curriculumDescriptions.ts);
 * this only has to read sensibly and never dangle ("Learn about Science in").
 */
export function getTopicDescription(topicName: string, subjectName: string): string {
  const subject = (subjectName || "").trim();
  if (!subject || subject.toLowerCase() === "subject") {
    return `Quizzes, lessons and reading for ${topicName}.`;
  }
  return `${topicName} practice questions in ${subject}.`;
}

/**
 * Get complete metadata for a topic. Pass the API's `description` when you have it.
 */
export function getTopicMetadata(topicName: string, subjectName: string, index: number = 0, description?: string | null): TopicMetadata {
  return {
    icon: getTopicIcon(topicName),
    color: getTopicColor(topicName, index),
    description: description?.trim() || getTopicDescription(topicName, subjectName),
  };
}
